import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { fileExtension, hasClientPreview } from "../formats";

export type ViewerSize = { x: number; y: number; z: number };

export type BedSize = { x: number; y: number; z: number };

/** Print-bed coordinates: X/Y on plate, Z = height (Three.js Y-up). */
export type BedFit = {
  fits: boolean;
  size: ViewerSize;
  bed: BedSize;
  overflows: { x: boolean; y: boolean; z: boolean };
  message: string;
};

const DEFAULT_BED: BedSize = { x: 340, y: 320, z: 340 };
const FIT_COLOR = 0x9ca3af;
const OVER_COLOR = 0xd97706;

export class StlViewer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly controls: OrbitControls;
  private readonly root: THREE.Group;
  /** Z-up print space → Y-up Three.js (bed = XZ, height = Y). */
  private readonly printFrame: THREE.Group;
  private readonly bedGroup: THREE.Group;
  private mesh: THREE.Mesh | null = null;
  private baseGeometry: THREE.BufferGeometry | null = null;
  private meshColor = FIT_COLOR;
  private raf = 0;
  private disposed = false;
  private bed: BedSize = { ...DEFAULT_BED };
  private grid: THREE.GridHelper | null = null;
  private volumeHelper: THREE.LineSegments | null = null;
  private previewUnavailable = false;

  scale = 1;
  rotationX = 0;
  rotationY = 0;
  rotationZ = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(42, 1, 0.1, 10_000);
    this.camera.position.set(180, 140, 200);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.target.set(0, 20, 0);
    this.controls.minDistance = 5;
    this.controls.maxDistance = 5000;

    this.root = new THREE.Group();
    this.scene.add(this.root);

    this.printFrame = new THREE.Group();
    // STL / Prusa / auto-orient bake are Z-up; Three.js bed is Y-up.
    this.printFrame.rotation.x = -Math.PI / 2;
    this.root.add(this.printFrame);

    this.bedGroup = new THREE.Group();
    this.scene.add(this.bedGroup);
    this.rebuildBed();

    const hemi = new THREE.HemisphereLight(0xffffff, 0xd1d5db, 1.05);
    this.scene.add(hemi);
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(80, 120, 60);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xe5e7eb, 0.55);
    fill.position.set(-60, 40, -80);
    this.scene.add(fill);

    this.resize();
    this.loop();
  }

  setBedSize(bed: BedSize): void {
    this.bed = {
      x: Math.max(1, bed.x),
      y: Math.max(1, bed.y),
      z: Math.max(1, bed.z),
    };
    this.rebuildBed();
  }

  getBedSize(): BedSize {
    return { ...this.bed };
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    if (w < 2 || h < 2) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  async loadFile(file: File, companions: File[] = []): Promise<ViewerSize> {
    const ext = fileExtension(file.name);
    if (!hasClientPreview(file.name)) {
      this.clearMesh();
      this.previewUnavailable = true;
      return { x: 0, y: 0, z: 0 };
    }

    this.previewUnavailable = false;
    const geometry =
      ext === ".obj"
        ? await this.loadObjGeometry(file, companions)
        : await this.loadStlGeometry(file);

    return this.displayGeometry(geometry);
  }

  /** Load STL produced by the server preview endpoint (CAD conversion). */
  async loadPreviewStl(blob: Blob): Promise<ViewerSize> {
    this.previewUnavailable = false;
    const buffer = await blob.arrayBuffer();
    const geometry = new STLLoader().parse(buffer);
    return this.displayGeometry(geometry);
  }

  /** Clear the viewport when server CAD preview is unavailable. */
  markPreviewUnavailable(): void {
    this.clearMesh();
    this.previewUnavailable = true;
  }

  private displayGeometry(geometry: THREE.BufferGeometry): ViewerSize {
    geometry.computeVertexNormals();
    geometry.center();
    geometry.computeBoundingBox();

    this.clearMesh();
    this.baseGeometry = geometry;

    const material = new THREE.MeshStandardMaterial({
      color: this.meshColor,
      metalness: 0.12,
      roughness: 0.42,
      flatShading: false,
      transparent: true,
      opacity: 0,
    });
    this.mesh = new THREE.Mesh(geometry, material);
    // Euler on mesh stays in print (Z-up) space — same as backend transform_mesh.
    this.printFrame.add(this.mesh);

    this.applyTransform();
    this.fitCamera();
    this.fadeIn(material);

    return this.measurePrint();
  }

  hasPreview(): boolean {
    return !this.previewUnavailable && this.mesh !== null;
  }

  private async loadStlGeometry(file: File): Promise<THREE.BufferGeometry> {
    const buffer = await file.arrayBuffer();
    const loader = new STLLoader();
    return loader.parse(buffer);
  }

  private async loadObjGeometry(
    file: File,
    companions: File[],
  ): Promise<THREE.BufferGeometry> {
    const mtlFile = companions.find((f) => fileExtension(f.name) === ".mtl");
    const objUrl = URL.createObjectURL(file);
    const objLoader = new OBJLoader();
    let object: THREE.Object3D;

    try {
      if (mtlFile) {
        const mtlUrl = URL.createObjectURL(mtlFile);
        try {
          const mtlText = await mtlFile.text();
          const mtlLoader = new MTLLoader();
          mtlLoader.setMaterialOptions({ normalizeRGB: true });
          const materials = mtlLoader.parse(mtlText, "");
          materials.preload();
          objLoader.setMaterials(materials);
        } finally {
          URL.revokeObjectURL(mtlUrl);
        }
      }

      object = await objLoader.loadAsync(objUrl);
    } finally {
      URL.revokeObjectURL(objUrl);
    }

    const geometries: THREE.BufferGeometry[] = [];
    object.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        geometries.push(child.geometry);
      }
    });
    if (!geometries.length) {
      throw new Error("Plik OBJ nie zawiera geometrii.");
    }
    return geometries.length === 1
      ? geometries[0]
      : mergeGeometries(geometries, false)!;
  }

  private clearMesh(): void {
    if (this.mesh) {
      this.printFrame.remove(this.mesh);
      this.mesh.geometry.dispose();
      const mat = this.mesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
      this.mesh = null;
    }
    this.baseGeometry?.dispose();
    this.baseGeometry = null;
  }

  setColorHex(hex: string): void {
    this.meshColor = new THREE.Color(hex).getHex();
    if (!this.mesh) return;
    const fit = this.checkBedFit();
    (this.mesh.material as THREE.MeshStandardMaterial).color.setHex(
      fit.fits ? this.meshColor : OVER_COLOR,
    );
  }

  applyTransform(overrideScale?: number): void {
    if (!this.mesh) return;
    const s = overrideScale ?? this.scale;
    this.mesh.position.set(0, 0, 0);
    this.mesh.scale.setScalar(s);
    this.mesh.rotation.set(
      THREE.MathUtils.degToRad(this.rotationX),
      THREE.MathUtils.degToRad(this.rotationY),
      THREE.MathUtils.degToRad(this.rotationZ),
    );
    this.placeOnBed();
    this.syncOverflowStyle();
  }

  refit(): void {
    this.applyTransform();
    this.fitCamera();
  }

  /** World AABB mapped to print axes: X/Y plate, Z height. */
  measurePrint(): ViewerSize {
    if (!this.mesh) return { x: 0, y: 0, z: 0 };
    this.mesh.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(this.mesh);
    const size = box.getSize(new THREE.Vector3());
    return {
      x: Math.round(size.x * 1000) / 1000,
      y: Math.round(size.z * 1000) / 1000,
      z: Math.round(size.y * 1000) / 1000,
    };
  }

  checkBedFit(): BedFit {
    const size = this.measurePrint();
    const overflows = {
      x: size.x > this.bed.x + 0.05,
      y: size.y > this.bed.y + 0.05,
      z: size.z > this.bed.z + 0.05,
    };
    const fits = !overflows.x && !overflows.y && !overflows.z;
    const parts: string[] = [];
    if (overflows.x) parts.push(`X ${size.x.toFixed(1)} > ${this.bed.x} mm`);
    if (overflows.y) parts.push(`Y ${size.y.toFixed(1)} > ${this.bed.y} mm`);
    if (overflows.z) parts.push(`Z ${size.z.toFixed(1)} > ${this.bed.z} mm`);
    const message = fits
      ? `Mieści się na stole ${this.bed.x}×${this.bed.y}×${this.bed.z} mm`
      : `Poza stołem: ${parts.join(", ")}`;
    return { fits, size, bed: { ...this.bed }, overflows, message };
  }

  private syncOverflowStyle(): void {
    if (!this.mesh) return;
    const fit = this.checkBedFit();
    const mat = this.mesh.material as THREE.MeshStandardMaterial;
    mat.color.setHex(fit.fits ? this.meshColor : OVER_COLOR);
    if (this.volumeHelper) {
      const colors = this.volumeHelper.geometry.getAttribute("color");
      if (colors) {
        const c = new THREE.Color(fit.fits ? 0x0055d4 : 0xd97706);
        for (let i = 0; i < colors.count; i++) {
          colors.setXYZ(i, c.r, c.g, c.b);
        }
        colors.needsUpdate = true;
      }
    }
  }

  private rebuildBed(): void {
    while (this.bedGroup.children.length) {
      const child = this.bedGroup.children[0];
      this.bedGroup.remove(child);
      if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
        child.geometry.dispose();
        const m = child.material;
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m.dispose();
      }
    }
    this.grid = null;
    this.volumeHelper = null;

    const { x, y, z } = this.bed;
    const plateSize = Math.max(x, y);

    this.grid = new THREE.GridHelper(plateSize, 32, 0xcbd5e1, 0xe5e7eb);
    this.grid.position.y = 0;
    this.bedGroup.add(this.grid);

    // Opaque bed plate (XY in print → XZ in Three.js)
    const plate = new THREE.Mesh(
      new THREE.PlaneGeometry(x, y),
      new THREE.MeshStandardMaterial({
        color: 0xf3f4f6,
        metalness: 0.02,
        roughness: 0.92,
        transparent: true,
        opacity: 0.7,
      }),
    );
    plate.rotation.x = -Math.PI / 2;
    plate.position.y = 0.02;
    this.bedGroup.add(plate);

    // Print volume wireframe: width X, depth Z, height Y
    const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(x, z, y));
    const colors = new Float32Array(edges.attributes.position.count * 3);
    const c = new THREE.Color(0x0f6e64);
    for (let i = 0; i < colors.length; i += 3) {
      colors[i] = c.r;
      colors[i + 1] = c.g;
      colors[i + 2] = c.b;
    }
    edges.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    this.volumeHelper = new THREE.LineSegments(
      edges,
      new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.85 }),
    );
    this.volumeHelper.position.y = z / 2;
    this.bedGroup.add(this.volumeHelper);

    // Corner markers on bed
    const markerMat = new THREE.MeshBasicMaterial({ color: 0x0f6e64 });
    for (const [mx, mz] of [
      [-x / 2, -y / 2],
      [x / 2, -y / 2],
      [-x / 2, y / 2],
      [x / 2, y / 2],
    ] as const) {
      const m = new THREE.Mesh(new THREE.BoxGeometry(3, 1.5, 3), markerMat);
      m.position.set(mx, 0.8, mz);
      this.bedGroup.add(m);
    }
  }

  private placeOnBed(): void {
    if (!this.mesh) return;
    this.mesh.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(this.mesh);
    if (!Number.isFinite(box.min.y)) return;
    // printFrame maps local Z → world Y; lower the mesh onto the plate.
    this.mesh.position.z -= box.min.y;
  }

  private fitCamera(): void {
    // Frame bed + model together
    const bedBox = new THREE.Box3(
      new THREE.Vector3(-this.bed.x / 2, 0, -this.bed.y / 2),
      new THREE.Vector3(this.bed.x / 2, this.bed.z, this.bed.y / 2),
    );
    let box = bedBox.clone();
    if (this.mesh) {
      this.mesh.updateMatrixWorld(true);
      box = box.union(new THREE.Box3().setFromObject(this.mesh));
    }
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z, 1);

    const fov = THREE.MathUtils.degToRad(this.camera.fov);
    const fitHeightDist = maxDim / 2 / Math.tan(fov / 2);
    const fitWidthDist = fitHeightDist / Math.max(this.camera.aspect, 0.1);
    const dist = Math.max(fitHeightDist, fitWidthDist) * 1.45;

    this.controls.target.copy(center);
    this.camera.position.set(
      center.x + dist * 0.75,
      center.y + dist * 0.55,
      center.z + dist * 0.75,
    );
    this.camera.near = Math.max(0.1, dist / 100);
    this.camera.far = Math.max(2000, dist * 20);
    this.camera.updateProjectionMatrix();
    this.controls.minDistance = Math.max(2, maxDim * 0.25);
    this.controls.maxDistance = Math.max(800, dist * 8);
    this.controls.update();
  }

  private fadeIn(material: THREE.MeshStandardMaterial): void {
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 380);
      material.opacity = t;
      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        material.transparent = false;
        material.opacity = 1;
      }
    };
    requestAnimationFrame(tick);
  }

  private loop = (): void => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.controls.dispose();
    this.renderer.dispose();
    this.baseGeometry?.dispose();
    if (this.mesh) {
      (this.mesh.material as THREE.Material).dispose();
    }
  }
}

export const COLOR_HEX: Record<string, string> = {
  black: "#222826",
  white: "#eef2ec",
  grey: "#8a948c",
  red: "#c23b2a",
  blue: "#2a5f9e",
  transparent: "#a8d4ce",
};
